import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react/ArrowsClockwise";
import { PairMobileDeviceQrCard } from "./CustomComponents/PairMobileDeviceQrCard";
import { PairMobileDeviceRefreshNotice } from "./CustomComponents/PairMobileDeviceRefreshNotice";
import { usePairMobileDevice } from "./Hooks/usePairMobileDevice";

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function PairMobileDeviceContent_() {
  const {
    pairing,
    loading,
    error,
    secondsRemaining,
    isExpired,
    justRegenerated,
    regenerate,
  } = usePairMobileDevice();

  if (error) {
    return (
      <Stack gap={4} align="center">
        <T color="red" align="center">
          Something went wrong creating a pairing code. Please try again.
        </T>
        <Button
          icon={ArrowsClockwiseIcon}
          onClick={regenerate}
          data-testid="pair-mobile-device-retry-button"
        >
          Try again
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={6} align="center">
      <T color="secondary" align="center">
        Open the FreightSimple app on the device, tap Pair, and scan this code.
      </T>

      <PairMobileDeviceQrCard nonce={pairing?.nonce} dimmed={isExpired} />

      <Stack gap={2} align="center">
        {isExpired ? (
          <Stack gap={1} align="center">
            <T size="lg" weight="semibold">
              This code has expired
            </T>
            <T size="sm" color="muted">
              Generate a new code to pair your device.
            </T>
          </Stack>
        ) : (
          <span data-testid="pair-mobile-device-countdown">
            <T size="sm" color="muted">
              {pairing === undefined
                ? "Generating code…"
                : `Expires in ${formatCountdown(secondsRemaining)}`}
            </T>
          </span>
        )}

        <PairMobileDeviceRefreshNotice visible={justRegenerated} />
      </Stack>

      <Button
        primary={isExpired}
        icon={ArrowsClockwiseIcon}
        loading={loading}
        onClick={regenerate}
        data-testid="pair-mobile-device-regenerate-button"
      >
        Generate new code
      </Button>
    </Stack>
  );
}

export function PairMobileDeviceScreen() {
  return (
    <FullWidthLayout
      header="Pair a mobile device"
      microcopy="Connect the FreightSimple mobile app to your account"
      content={<PairMobileDeviceContent_ />}
    />
  );
}
